import { DataTable, LinkButton, Tag, dataColumns, formatShortDate, type DataColumn } from "@amber/ui";
import type { QuickAction, TaskRow } from "../../contracts/index";
import { waitingFor } from "../shared";
import { HandledIn, Subject, TaskTitle } from "./cells";
import { PriorityCell } from "./PriorityCell";
import { QuickActions } from "./QuickActions";

const col = dataColumns<TaskRow>();

const typeColumn = (width: number) =>
  col.display({ id: "type", header: "Type", meta: { width }, cell: ({ row }) => row.original.type_label });
const subjectColumn = col.display({
  id: "subject",
  header: "Subject",
  meta: { width: 100 },
  cell: ({ row }) => <Subject task={row.original} />,
});
const affectedColumn = (width: number) =>
  col.display({ id: "affected", header: "Affected", meta: { width }, cell: ({ row }) => row.original.affected.label });
const handledColumn = (width: number) =>
  col.display({
    id: "handled",
    header: "Handled in",
    meta: { width, interactive: true },
    cell: ({ row }) => <HandledIn task={row.original} />,
  });

const DONE_COLUMNS: DataColumn<TaskRow>[] = [
  col.display({
    id: "task",
    header: "Task",
    meta: { width: 300 },
    cell: ({ row }) => <TaskTitle task={row.original} />,
  }),
  typeColumn(150),
  subjectColumn,
  affectedColumn(90),
  col.display({
    id: "resolved",
    header: "Resolved",
    meta: { width: 170 },
    cell: ({ row: { original: row } }) => (
      <>
        {row.closed?.how === "closed_automatically" ? <Tag tone="quiet">Automatically</Tag> : null} {row.closed?.reason}
      </>
    ),
  }),
  col.display({ id: "by", header: "By", meta: { width: 130 }, cell: ({ row }) => row.original.closed?.by }),
  col.display({
    id: "closed",
    header: "Closed",
    meta: { width: 140 },
    cell: ({ row: { original: row } }) =>
      row.closed ? `${formatShortDate(row.closed.at)} · ${waitingFor(row.opened_at, new Date(row.closed.at))}` : "—",
  }),
  handledColumn(140),
];

function openColumns(onAct: (task: TaskRow, action: QuickAction) => void): DataColumn<TaskRow>[] {
  return [
    col.display({
      id: "priority",
      header: "Priority",
      meta: { width: 96, cellClassName: "overflow-visible", interactive: true },
      cell: ({ row: { original: row } }) =>
        row.priority ? <PriorityCell priority={row.priority} openedAt={row.opened_at} /> : null,
    }),
    col.display({
      id: "task",
      header: "Task",
      meta: { width: 240 },
      cell: ({ row }) => <TaskTitle task={row.original} />,
    }),
    typeColumn(130),
    subjectColumn,
    affectedColumn(84),
    col.display({
      id: "waiting",
      header: "Waiting since",
      meta: { width: 96 },
      cell: ({ row }) => waitingFor(row.original.opened_at),
    }),
    handledColumn(130),
    col.display({
      id: "quick",
      header: "Quick action",
      meta: { width: 170, cellClassName: "overflow-visible" },
      cell: ({ row: { original: row } }) => (
        <QuickActions
          task={row}
          onAction={(action) => {
            onAct(row, action);
          }}
        />
      ),
    }),
  ];
}

export function TasksTable({
  view,
  rows,
  pending,
  noMatches,
  hidden,
  taskId,
  onReset,
  onOpenTask,
  onAct,
}: {
  view: "open" | "done";
  rows: TaskRow[];
  pending: boolean;
  noMatches: boolean;
  hidden: ReadonlySet<string>;
  taskId: string | null;
  onReset: () => void;
  onOpenTask: (id: string | null) => void;
  onAct: (task: TaskRow, action: QuickAction) => void;
}) {
  const done = view === "done";
  return (
    <DataTable
      label={done ? "Done tasks" : "Open tasks"}
      columns={done ? DONE_COLUMNS : openColumns(onAct)}
      rows={rows}
      rowId={(r) => r.id}
      minWidth={done ? 1000 : 1040}
      pinned={[done ? "task" : "priority"]}
      hidden={hidden}
      selectedId={taskId}
      isPending={pending}
      rowOptions={(row) => {
        const open = () => {
          onOpenTask(row.id);
        };
        return {
          label: row.title,
          onOpen: open,
          onPick: open,
          ...(done ? { titles: { resolved: row.closed?.reason } } : {}),
        };
      }}
      empty={
        noMatches ? (
          <>
            No tasks match these filters. <LinkButton onClick={onReset}>Reset</LinkButton>
          </>
        ) : null
      }
    />
  );
}
